import AppLogoIcon from "~/components/app-logo-icon";
import { FlashToaster } from "~/components/flash-toaster";
import LanguageSwitcher from "~/components/LanguageSwitcher";
import { Toaster } from "~/components/ui/sonner";
import { urlFor } from "~/client";
import { useAppearance } from "~/hooks/use-appearance";
import { cn } from "~/lib/utils";
import { Link } from "@inertiajs/react";
import { useEffect, useState, type CSSProperties, type PropsWithChildren } from "react";

interface AuthLayoutProps {
  name?: string;
  title?: string;
  description?: string;
  variant?: "default" | "light";
  branding?: {
    companyName: string;
    logoUrl: string | null;
    primaryColor: string;
    primaryForeground: "#111827" | "#ffffff";
  };
}

const darkLogo = "/AIRIE_LOGO ICON BLANCO.webp";
const lightLogo = "/AIRIE_ICON .webp";

export default function AuthSimpleLayout({
  children,
  title,
  description,
  variant = "default",
  branding,
}: PropsWithChildren<AuthLayoutProps>) {
  const { appearance } = useAppearance();
  const [mounted, setMounted] = useState(false);

  const appLogo = mounted && appearance === "dark" && variant !== "light" ? darkLogo : lightLogo;
  const brandStyle = branding
    ? ({
        "--primary": branding.primaryColor,
        "--primary-foreground": branding.primaryForeground,
        "--ring": branding.primaryColor,
      } as CSSProperties)
    : undefined;

  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div
      className={cn(
        "flex h-screen flex-col items-center overflow-auto p-6 md:p-10",
        variant === "light" ? "bg-slate-50 text-slate-950" : "bg-background",
      )}
      style={brandStyle}
    >
      <FlashToaster />
      <Toaster position="top-right" richColors />
      <div className="absolute top-6 right-6 z-50 [&_[data-slot=button]]:!bg-white">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md py-20">
        <div className="flex flex-col gap-8">
          <div className="flex flex-col items-center gap-4">
            <Link href={urlFor("home")} className="flex flex-col items-center gap-2 font-medium">
              <div className="mb-1 flex h-24 w-auto items-center justify-center rounded-md">
                {branding?.logoUrl ? (
                  <img
                    src={branding.logoUrl}
                    alt={branding.companyName}
                    className="h-24 max-w-full object-contain"
                  />
                ) : (
                  <AppLogoIcon
                    className={cn(
                      "h-24 w-auto fill-current text-[var(--foreground)]",
                      variant === "light" ? "text-slate-950" : "dark:text-white",
                    )}
                    src={appLogo}
                  />
                )}
              </div>
              <span className="sr-only">{title}</span>
            </Link>

            <div className="space-y-2 text-center">
              <h1 className="text-xl font-medium">{title}</h1>
              <p
                className={cn(
                  "text-center text-sm",
                  variant === "light" ? "text-slate-600" : "text-muted-foreground",
                )}
              >
                {description}
              </p>
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}
